import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getMessagingPeople } from 'services/messagingHistory'
import AnnounceArchive from './archive'
import EventsTab from './EventsTab'
import PeopleTab from './PeopleTab'

type TView = 'people' | 'events'

const VIEWS: { key: TView; label: string }[] = [
    { key: 'people', label: 'اعضا' },
    { key: 'events', label: 'آخرین پیام‌ها' },
]

type TProbe =
    | { state: 'loading' }
    | { state: 'ready'; data: IMessagingPeopleResponse }
    | { state: 'archive' }

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status ?? null

/**
 * «اطلاع‌رسانی به اعضا» — everything Hamyan told this fund's members.
 *
 * Two readings of one log: «اعضا» asks what a given person has been told (the
 * manager's real question — three reminders, all delivered, none opened), and
 * «آخرین پیام‌ها» asks what went out. The tab lives in `?view=` so going back
 * to this page, or reloading it, lands where it was left.
 *
 * DEPLOY ORDER. This build may reach members before the backend that serves
 * `/messaging/history/`. The first people request is therefore also the probe:
 * if it does not come back in the new shape — 404 from an older backend, or
 * anything else — the page silently renders the old announce list instead, as
 * it was. It is the same screen that lives at `…/archive`, which stays
 * reachable either way, because the new log only holds what was sent after it
 * went live.
 */
export default function AnnounceToMembers() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [probe, setProbe] = useState<TProbe>({ state: 'loading' })

    const view = (VIEWS.find((item) => item.key === searchParams.get('view'))
        ?.key ?? 'people') as TView

    useEffect(() => {
        if (!id) return
        let alive = true

        const fallBack = (status: number | null) => {
            if (!alive) return
            posthog.capture('announce_history_load_failed', {
                screen: 'people',
                moneypool_id: Number(id),
                page: 1,
                status,
            })
            posthog.capture('announce_history_fallback_to_archive', {
                moneypool_id: Number(id),
                status,
            })
            setProbe({ state: 'archive' })
        }

        getMessagingPeople(id, { sort: 'recent', page: 1 })
            .then((data) => {
                if (!alive) return
                // A 200 that is not this endpoint's answer — a proxy page, an
                // older route that matched — is as good as no endpoint.
                if (!data || !Array.isArray(data.results)) return fallBack(null)
                setProbe({ state: 'ready', data })
            })
            .catch((error) => fallBack(statusOf(error)))

        return () => {
            alive = false
        }
    }, [id])

    const choose = (next: TView) => {
        if (next === view) return
        const params = new URLSearchParams(searchParams)
        params.set('view', next)
        // replace: a tab is a view of this page, not a place of its own —
        // back should leave the screen, not walk the tabs again
        setSearchParams(params, { replace: true })
    }

    if (probe.state === 'archive') return <AnnounceArchive />

    // Nothing but the header until the probe answers: the tabs would otherwise
    // appear for an instant and be replaced by a screen that has none.
    if (probe.state === 'loading')
        return (
            <div className=" min-h-screen bg-canvas">
                <Header title="اطلاع‌رسانی به اعضا" />
                <div className=" pt-6">
                    <DotsLoading />
                </div>
            </div>
        )

    return (
        <div className=" min-h-screen bg-canvas pb-10">
            <Header title="اطلاع‌رسانی به اعضا" />

            <div className=" sticky top-14 z-10 bg-canvas px-3 pb-2 pt-3">
                <div
                    role="tablist"
                    aria-label="نمای اطلاع‌رسانی"
                    className=" flex rounded-full border border-stroke bg-white p-1"
                >
                    {VIEWS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            role="tab"
                            aria-selected={view === item.key}
                            onClick={() => choose(item.key)}
                            className={` flex-1 rounded-full py-2 text-[12px] transition-colors ${
                                view === item.key
                                    ? ' bg-green-1 font-bold text-white'
                                    : ' text-text-secondary'
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className=" px-3" role="tabpanel">
                {view === 'people' ? (
                    <PeopleTab fundId={String(id)} initial={probe.data} />
                ) : (
                    <EventsTab fundId={String(id)} />
                )}
            </div>

            <div className=" px-3 pt-2 text-center">
                <button
                    type="button"
                    onClick={() => navigate(`/fund/announce/${id}/archive`)}
                    className=" text-xs text-text-secondary underline underline-offset-4"
                >
                    پیامک‌های قدیمی‌تر
                </button>
            </div>
        </div>
    )
}
